import React, { useMemo, useState } from 'react';
import { getLocalizedLabel } from '../../../../helpers/localize';
import {
  ASSESSMENT_STATUSES,
  ASSESSMENT_STATUSES_OPTIONS,
  INVITATION_TYPE,
} from '../../constants';
import { ReactComponent as PendingIcon } from '../../../../assets/icons/pending.svg';
import { ReactComponent as EditIcon } from '../../../../assets/icons/edit.svg';
import { ReactComponent as InfoIcon } from '../../../../assets/icons/info_gray.svg';
import { ReactComponent as DownloadIcon } from '../../../../assets/icons/file_download_black.svg';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import VisibilityIcon from '@material-ui/icons/Visibility';
import MenuItem from '../../../Shared/Table/widgets/MenuItem';
import {
  enqueueSnackbar,
  SNACKBAR_VARIANTS,
} from '../../../../redux_store/reducer/reducers/notificationsReducer';
import showServerError from '../../../../helpers/showError';
import { useMutation } from 'react-fetching-library';
import { downloadFile } from '../../../../helpers/DownloadPDF';
import { checkPermission } from '../../../../helpers/checkPermissions';
import permissions from '../../../../config/permissions';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateCanShowShimmer
} from '../../../../redux_store/reducer/reducers/tableReducer/tableReducer';

const RowActions = ({
                      row,
                      setAnchorEl,
                      toggleConfirmResentDialog,
                      reloadData,
                      setEnableClaimModalInfo,
                      setAssessmentLinkForShareModal,
                      setIsDataChangingRequesting,
                      dataLoading,
                      modifiedAssessments,
                    }) => {
  const {
    en: {
      titles,
      errors,
      notifications,
    },
  } = useSelector((state) => state.i18n);
  const {
    metadata: {
      currentAccountPermissions,
    },
  } = useSelector((state) => state.user);
  const hasLoadFilesPermission = checkPermission(
    currentAccountPermissions,
    permissions.SURVEYS_GET,
  );
  const [isNoShowLoading, setIsNoShowLoading] = useState(false);
  const dispatch = useDispatch();
  const checkIsDisabled = (assessmentUuid) => {
    if (dataLoading || !modifiedAssessments) {
      return true;
    }
    let isDisabled = false;

    modifiedAssessments.forEach((assessment) => {
      if (assessment.uuid === assessmentUuid) {
        const allowed = assessment.claimable && assessment.status === getLocalizedLabel(
          ASSESSMENT_STATUSES_OPTIONS,
          ASSESSMENT_STATUSES.completed,
        );
        isDisabled = !allowed;
      }
    });
    return isDisabled;
  };

  const checkIsResendingDisabled = (assessmentUuid) => {
    if (dataLoading || !modifiedAssessments) {
      return true;
    }
    let isDisabled = false;

    modifiedAssessments.forEach((assessment) => {
      if (assessment.uuid === assessmentUuid) {
        isDisabled = assessment.invitationType === INVITATION_TYPE.notAllowed;
      }
    });

    return isDisabled;
  };

  const checkIsDownloadProgramPDFDisabled = (assessmentUuid) => {
    if (dataLoading || !modifiedAssessments || !hasLoadFilesPermission) {
      return true;
    }
    let isDisabled = true;

    modifiedAssessments.forEach((assessment) => {
      if (assessment.uuid === assessmentUuid && assessment.status === getLocalizedLabel(
        ASSESSMENT_STATUSES_OPTIONS,
        ASSESSMENT_STATUSES.completed,
      )) {
        isDisabled = false;
      }
    });

    return isDisabled;
  };

  const handleConfirmResentDialog = (e, props) => {
    toggleConfirmResentDialog({
      ...props,
      survey: props.uuid,
    });
  };

  const importFile = (assessment) => ({
    method: 'GET',
    endpoint: `/accounts/${assessment.accountUUID}/surveys/${assessment.uuid}/program-report/pdf`,
    responseType: 'blob',
  });

  const { mutate: importMutate, loading: isPDFLoading } = useMutation(importFile);

  const downloadProgramPDF = async (e, assessment) => {
    const { payload, error, status } = await importMutate(assessment);
    if (!error) {
      downloadFile(
        new Blob([payload]),
        `${assessment.surveyName} - ${assessment.patientLastName} ${assessment.patientFirstName} ${assessment.orderDate}.pdf`);
      dispatch(
        enqueueSnackbar({
          message: notifications.pdfSucceededSaved,
          options: {
            variant: SNACKBAR_VARIANTS.success,
          },
        }),
      );
    } else {
      switch (status) {
        case 422: {
          const obj = JSON.parse(await payload.text());
          Object.keys(obj.validationErrors).forEach(key =>
            showServerError(dispatch, obj.validationErrors[key]));
          break;
        }
        default: {
          showServerError(dispatch, errors.unknownError);
          break;
        }
      }
    }
    setAnchorEl(null);
  };

  const changeNoShowStatus = (assessment, urlEnd) => ({
    method: 'POST',
    endpoint: `/accounts/${assessment.accountUUID}/surveys/${assessment.uuid}/${urlEnd}`,
  });

  const { mutate: changeNoShow } = useMutation(changeNoShowStatus);

  const noShow = async (e, assessment) => {
    setIsNoShowLoading(true);
    const {
      payload,
      error,
      status,
    } = await changeNoShow(assessment, assessment.status === 'No Show' ? 'undo-no-show' : 'set-no-show');
    if (!error) {
      dispatch(updateCanShowShimmer(true));
      reloadData({ delay: 500 });
      dispatch(
        enqueueSnackbar({
          message: assessment.status === ASSESSMENT_STATUSES.noShow
            ? notifications.noShowStatusSucceededReversed
            : notifications.noShowStatusSucceededChanged,
          options: {
            variant: SNACKBAR_VARIANTS.success,
          },
        }),
      );
      setIsDataChangingRequesting(true);
      setTimeout(() => {
        setIsDataChangingRequesting(false);
        setIsNoShowLoading(false);
        setAnchorEl(null);
      }, 500);
    } else {
      switch (status) {
        case 422: {
          Object.keys(payload.validationErrors).forEach(key =>
            showServerError(dispatch, payload.validationErrors[key]));
          break;
        }
        default: {
          showServerError(dispatch, errors.unknownError);
          break;
        }
      }
      setAnchorEl(null);
    }
  };

  const checkIsNoShowDisabled = (assessmentUuid) => {
    if (dataLoading || !modifiedAssessments) {
      return true;
    }
    let isDisabled = false;

    modifiedAssessments.forEach((assessment) => {
      if (assessment.uuid === assessmentUuid) {
        const appointmentDate = new Date(assessment.appointmentTimestamp);
        const today = new Date();
        const allowed = assessment.appointmentTimestamp && appointmentDate <= today || assessment.status === ASSESSMENT_STATUSES.noShow;
        isDisabled = !allowed;
      }
    });
    return isDisabled;
  };

  const actions = [
    {
      key: 'resendInvitationAction',
      icon: <PendingIcon />,
      onActionClick: handleConfirmResentDialog,
      children: titles.resendInvitation,
      isDisabled: checkIsResendingDisabled,
    },
    {
      key: 'claimableAction',
      icon: <EditIcon />,
      onActionClick: (e, claim) => {
        setEnableClaimModalInfo(claim);
      },
      children: titles.generateClaim,
      isDisabled: checkIsDisabled,
    },
    {
      key: 'viewAssessmentLink',
      icon: <InfoIcon />,
      onActionClick: (e, claim) => {
        setAssessmentLinkForShareModal(claim.url);
      },
      children: titles.viewAssessmentLink,
    },
    {
      key: 'downloadProgram',
      icon: <DownloadIcon />,
      onActionClick: downloadProgramPDF,
      children: titles.downloadProgram,
      isDisabled: checkIsDownloadProgramPDFDisabled,
      setAnchorElNull: true,
    },
    {
      key: 'No Show',
      icon: <VisibilityOffIcon style={{ fontSize: 16 }} />,
      onActionClick: noShow,
      children: titles.noShow,
      isDisabled: checkIsNoShowDisabled,
      showConditionFlag: 'canRevertNoShowStatus',
      showConditionValue: false,
      setAnchorElNull: true,
    },
    {
      key: 'Reverse No Show',
      icon: <VisibilityIcon style={{ fontSize: 16 }} />,
      onActionClick: noShow,
      children: titles.reverseNoShow,
      showConditionFlag: 'canRevertNoShowStatus',
      showConditionValue: true,
      setAnchorElNull: true,
    },
  ];

  const children = useMemo(
    () => actions.map((props) => {
      const {
        key,
        children,
        onActionClick,
        icon,
        addDivider,
        isRemove,
        isDisabled,
        showConditionFlag,
        showConditionValue,
        setAnchorElNull,
      } = props;
      return <MenuItem
        title={children}
        icon={icon}
        setAnchorEl={setAnchorElNull ? () => {
        } : setAnchorEl}
        onClick={(e) => onActionClick(e, row.original)}
        hasTopDriver={addDivider}
        isErrorStyle={isRemove}
        isLoading={
          (isPDFLoading && key === 'downloadProgram')
          || (isNoShowLoading && (key === 'No Show' || key === 'Reverse No Show'))}
        isDisabled={isDisabled}
        rowUuid={row.original.uuid}
        showConditionFlag={row.original[showConditionFlag]}
        showConditionValue={showConditionValue}
      />;
    }),
    [row, isPDFLoading, isNoShowLoading],
  );

  return <>{children}</>;
};

export default RowActions;
